import { i18n } from "../../translate/i18n";

export const STAGES = ["before", "due", "after"];

export const TEMPLATE_VARIABLES = [
  "{nome}",
  "{vencimento}",
  "{valor}",
  "{linhaDigitavel}",
  "{pix}",
  "{link}",
  "{contrato}",
];

export const HOURS = Array.from({ length: 24 }, (_, h) => h);

// Backend accepts offsetDays between 1 and 90 for 'before'/'after'
export const MAX_OFFSET_DAYS = 90;

export const ROWS_PER_PAGE = 10;

// Only WhatsApp connections (Baileys or official) can send billing messages;
// facebook/instagram connections are also returned by GET /whatsapp.
export const isWhatsappConnection = (w) =>
  !w?.channel || String(w.channel).toLowerCase().includes("whatsapp");

export const isOfficialChannel = (w) =>
  !!w && String(w.channel || "").toLowerCase().includes("oficial");

export const formatHour = (hour) => {
  const h = Number(hour);
  if (!Number.isInteger(h) || h < 0 || h > 23) return "-";
  return `${String(h).padStart(2, "0")}:00`;
};

export const stageLabel = (stage, offsetDays) => {
  const days = Number(offsetDays) || 0;
  if (stage === "before") return i18n.t("billingRules.stage.beforeDays", { count: days });
  if (stage === "after") return i18n.t("billingRules.stage.afterDays", { count: days });
  return i18n.t("billingRules.stage.due");
};

// Short D-3 / D0 / D+5 notation used next to the readable label
export const stageCode = (stage, offsetDays) => {
  const days = Number(offsetDays) || 0;
  if (stage === "before") return `D-${days}`;
  if (stage === "after") return `D+${days}`;
  return "D0";
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const SKIPPED_REASONS = ["dedup", "optout", "noPhone", "needsTemplate"];

// Run totals come from BillingRuleRun.totals:
//   { candidates, items, pending, skipped: { optout, dedup, needsTemplate, noPhone } }
// sent/failed live on the linked InvoiceCampaign (totalSent/totalFailed) and are
// merged here when the campaign is available; otherwise they are null (unknown).
export const getTotals = (totals, campaign) => {
  const src = totals || {};
  const skippedSrc = src.skipped && typeof src.skipped === "object" ? src.skipped : {};
  const skippedBreakdown = {};
  let skipped = 0;
  Object.keys(skippedSrc).forEach((reason) => {
    const n = toNumber(skippedSrc[reason]);
    skippedBreakdown[reason] = n;
    skipped += n;
  });
  // Legacy/flat shape (skipped as a number)
  if (typeof src.skipped !== "object") skipped = toNumber(src.skipped);

  const total = src.items !== undefined ? toNumber(src.items) : toNumber(src.total);
  const camp = getCampaignTotals(campaign);
  const sent = camp ? camp.sent : src.sent !== undefined ? toNumber(src.sent) : null;
  const failed = camp ? camp.failed : src.failed !== undefined ? toNumber(src.failed) : null;
  let pending = toNumber(src.pending);
  if (camp) pending = Math.max(total - camp.sent - camp.failed - camp.skipped, 0);

  return {
    total,
    candidates: toNumber(src.candidates),
    pending,
    sent,
    failed,
    skipped,
    skippedBreakdown,
    campaignStatus: camp ? camp.status : null,
  };
};

// InvoiceCampaign counters (GET /isp/invoice-campaigns/:id): totalSent/totalFailed/
// totalSkipped; falls back to counting items[] by status.
export const getCampaignTotals = (campaign) => {
  if (!campaign || typeof campaign !== "object") return null;
  const items = Array.isArray(campaign.items) ? campaign.items : null;
  const count = (status) => (items ? items.filter((it) => it.status === status).length : 0);
  const pick = (field, status) =>
    campaign[field] !== undefined && campaign[field] !== null ? toNumber(campaign[field]) : count(status);
  return {
    sent: pick("totalSent", "sent"),
    failed: pick("totalFailed", "failed"),
    skipped: pick("totalSkipped", "skipped"),
    status: campaign.status || null,
  };
};

// "Duplicadas 2 · Sem telefone 1" — text for the skipped tooltip
export const skippedBreakdownText = (breakdown) => {
  const entries = Object.keys(breakdown || {}).filter((k) => breakdown[k] > 0);
  if (entries.length === 0) return "";
  return entries
    .map((reason) => {
      const key = `billingRules.skippedReasons.${reason}`;
      const label = i18n.exists(key) ? i18n.t(key) : reason;
      return `${label} ${breakdown[reason]}`;
    })
    .join(" · ");
};

export const formatCount = (n) => (n === null || n === undefined ? "–" : String(n));

export const formatAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value || "-";
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "BRL" });
  } catch (err) {
    return n.toFixed(2);
  }
};

// Converts a rule returned by GET /billing-rules into the PUT body
export const ruleToBody = (rule) => ({
  name: rule.name,
  erpIntegrationId: rule.erpIntegration?.id ?? rule.erpIntegrationId ?? null,
  whatsappId: rule.whatsapp?.id ?? rule.whatsappId ?? null,
  stage: rule.stage,
  offsetDays: rule.stage === "due" ? 0 : Number(rule.offsetDays) || 0,
  sendHour: Number(rule.sendHour) || 0,
  quickMessageId: rule.quickMessage?.id ?? rule.quickMessageId ?? null,
  textTemplate: rule.textTemplate ?? null,
  active: !!rule.active,
  sendPdf: !!rule.sendPdf,
  copyButtons: !!rule.copyButtons,
  filters: rule.filters || {},
});
